import { useCallback, useEffect, useState } from 'react'
import { Rocket } from 'lucide-react'
import {
  Bidi, Card, CardHeader, ErrorState, LoadingState, NoPermissionState, NumberField,
  useLocale,
} from '@hk/ui'
import {
  fetchGoLive, formatCount, may, nameIn, setGoLiveTarget, translateError,
  type GoLiveRow, type GoLiveState, type GoLiveStep, type GoLiveView,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-135 · WHAT HAS TO BE LOADED BEFORE THE FIRST SHIFT RUNS.  Sprint 53.

   ── WHY THIS SCREEN EXISTS, IN THE PACK'S OWN WORDS ──────────────────────────────────
   *"This screen exists for one week and is then never opened again. It is here because
   go-live data loading is consistently the thing nobody owns, and a checklist with counts
   against it is cheaper than a spreadsheet emailed round."*

   **THAT ONE WEEK IS WHY THE WORDS ON IT MATTER MORE THAN ON ANY OTHER SCREEN.** Nobody
   builds familiarity with a screen they open five times. Every label here has to be right
   the first time it is read, by somebody who has been handed a job nobody wanted.

   ── IT LINKS NOWHERE, AND THAT IS DELIBERATE ─────────────────────────────────────────
   The pack draws a table and no buttons. Every row here is somebody else's screen, and a
   checklist that offers eleven ways out of itself becomes a navigation menu with counts.
   The administrator loading data already knows where the rooms screen is; what they do not
   know is which of the eleven is short.

   ── AND IT NEVER DOES ANY OF THE WORK IT REPORTS ─────────────────────────────────────
   There is no "load the defaults for me". A screen whose job is to report what has been
   done, which can also do some of it, ends up reporting on itself — and the first row
   somebody cannot account for is the one it filled in.

   ── THE ONE THING IT WRITES IS A NUMBER ABOUT THE HOTEL ──────────────────────────────
   **Nothing in this product knows a hotel has 485 rooms until somebody says so.** Without
   that figure the screen can only report what has been loaded, which reads as finished on
   a hotel that loaded four hundred of its five hundred rooms and stopped — the single
   mistake this screen exists to prevent. So a target is typed, per row, and a row without
   one says `Loaded` rather than `Done`.

   Two rows are never typed because the product genuinely knows the answer: every room needs
   cleaning rules, every stock item needs an opening count. They are drawn without a box —
   offering somebody a control that cannot usefully change anything is worse than not
   offering one.
   ========================================================================= */

/** Named placeholders, because Arabic and English do not order their clauses the same. */
function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole)
}

/* **THE COLOURS ARE THE BOARD'S OWN**, so that a person who has spent the week on the room
   board reads them without learning anything new: amber is something to do, red is
   something that cannot be done, green is finished, grey is not yet anybody's problem. */
const TONE: Record<GoLiveState, { fg: string; bg: string; border: string }> = {
  DONE:        { fg: '#047857', bg: '#ecfdf5', border: '#6ee7b7' },
  SHORT:       { fg: '#92400e', bg: '#fffbeb', border: '#fcd34d' },
  LOADED:      { fg: '#1e40af', bg: '#eff6ff', border: '#93c5fd' },
  NOT_STARTED: { fg: '#92400e', bg: '#fffbeb', border: '#fcd34d' },
  WAITING:     { fg: '#475569', bg: '#f8fafc', border: '#cbd5e1' },
  BLOCKED:     { fg: '#b91c1c', bg: '#fef2f2', border: '#fca5a5' },
}

export function GoLive({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [view, setView] = useState<GoLiveView | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [saving, setSaving] = useState<GoLiveStep | null>(null)

  /* The typed figure per row while it is being edited. **Null is an empty box**, which is
     not the same as a zero — see `NumberField`. A row absent from this map is showing the
     server's answer; a row present is showing what somebody is typing. */
  const [draft, setDraft] = useState<Partial<Record<GoLiveStep, number | null>>>({})

  /* **THE GRANT GATES THE REQUEST, NOT ONLY THE DRAWING** — the same lesson as W-107. A
     screen that fires a request it knows will be refused fills an audit log with noise. */
  const allowed = may('admin.goLive')

  const load = useCallback(async () => {
    if (!allowed) return
    setError(null)
    try {
      setView(await fetchGoLive(propertyId))
    } catch (caught: unknown) {
      setError(caught)
    }
  }, [propertyId, allowed])

  useEffect(() => { void load() }, [load])

  if (!allowed) return <NoPermissionState />
  if (error !== null && view === null) {
    return <ErrorState body={translateError(error, locale).message} onRetry={() => void load()} />
  }
  if (view === null) return <LoadingState rows={6} />

  async function commit(row: GoLiveRow, typed: number | null) {
    /* **AN EMPTY BOX IS NOT A ZERO AND IS NOT A SAVE.** Somebody who tabs through the
       table without typing has said nothing, and a blank that cleared the target would
       undo yesterday's work by being walked past. Clearing a target is done by typing a
       nought, which is a deliberate act. */
    if (typed === null) {
      setDraft((d) => { const next = { ...d }; delete next[row.step]; return next })
      return
    }

    const expected = typed
    if (!Number.isInteger(expected) || expected < 0) return
    if (expected === (row.expected ?? 0)) {
      setDraft((d) => { const next = { ...d }; delete next[row.step]; return next })
      return
    }

    setSaving(row.step); setError(null)
    try {
      setView(await setGoLiveTarget(propertyId, row.step, expected))
      setDraft((d) => { const next = { ...d }; delete next[row.step]; return next })
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setSaving(null)
    }
  }

  const hasBlocked = view.rows.some((r) => r.state === 'BLOCKED')
  const hasUntargeted = view.rows.some((r) => r.state === 'LOADED')

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader title={t.glTitle} icon={<Rocket className="size-4" />} />
        <div className="flex flex-col gap-3 p-3.5">
          <p className="max-w-[70ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.glIntro}
          </p>

          {/* **THE HOTEL IS NAMED.** Najaf and Karbala go live separately and share
              nothing; a checklist that did not say which one it was counting would be read
              against the wrong hotel on the one week it is open. */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-[13.5px] font-semibold">
              <Bidi>{nameIn(locale, view.propertyName, view.propertyNameAr)}</Bidi>
            </span>
            <span className="text-[12.5px] text-muted-foreground">
              {fill(t.glProgress, {
                done: formatCount(view.completeCount, locale),
                total: formatCount(view.stepCount, locale),
              })}
            </span>
          </div>
        </div>
      </Card>

      {error !== null ? (
        <div
          role="alert"
          className="rounded-hk border border-[#fca5a5] bg-[#fef2f2] px-3 py-2 text-[12px]
                     text-[#b91c1c] dark:border-[#7f1d1d] dark:bg-[#2a0f0f] dark:text-[#fca5a5]"
        >
          {String(translateError(error, locale).message)}
        </div>
      ) : null}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="p-2 text-start font-medium">{t.glColWhat}</th>
                <th className="p-2 text-start font-medium">{t.glColSource}</th>
                <th className="p-2 text-start font-medium">{t.glColExpected}</th>
                <th className="p-2 text-start font-medium">{t.glColLoaded}</th>
                <th className="p-2 text-start font-medium">{t.glColDepends}</th>
                <th className="p-2 text-start font-medium">{t.glColState}</th>
              </tr>
            </thead>
            <tbody>
              {view.rows.map((row) => {
                const tone = TONE[row.state]
                const name = t.glStep[row.step]
                const missing = row.expected !== null && row.loaded < row.expected
                  ? row.expected - row.loaded
                  : 0

                return (
                  <tr key={row.step} className="border-b border-border/60 last:border-0">
                    <td className="p-2 font-medium"><Bidi>{name}</Bidi></td>
                    <td className="p-2 text-muted-foreground">
                      <Bidi>{t.glSource[row.source]}</Bidi>
                    </td>

                    <td className="p-2">
                      {/* **NO BOX ON THE TWO THE PRODUCT WORKS OUT.** Offering a control
                          that cannot usefully change anything is worse than not offering
                          one — and the word says why rather than leaving somebody to
                          wonder whether the box is missing or broken. */}
                      {row.derived ? (
                        <span className="text-muted-foreground">
                          {formatCount(row.expected ?? 0, locale)}
                          {' · '}
                          <Bidi>{t.glWorkedOut}</Bidi>
                        </span>
                      ) : (
                        /* **`NumberField`, NOT A BARE INPUT** — CQ-139, and the first
                           draft of this screen got it wrong in the way that component
                           exists to prevent: eleven boxes showing 4, 2, 40 in Western
                           digits, in a column beside ٤, ٢, ٤٠. Caught by looking at the
                           rendered Arabic screen, which is the only way this kind of
                           defect is ever caught.

                           **AND ITS OWN ACCESSIBLE NAME.** A visible legend in every cell
                           of a column would be the column header written eleven times; the
                           header alone is not a label either — #152's lesson exactly, since
                           eleven boxes all announcing "Should be" is eleven boxes nobody
                           can tell apart. Each carries its own row's question. */
                        <NumberField
                          aria-label={fill(t.glTargetLabel, { what: name })}
                          className="w-20"
                          min={0}
                          disabled={saving === row.step}
                          value={draft[row.step] !== undefined
                            ? draft[row.step] ?? null
                            : row.expected}
                          onChange={(next) =>
                            setDraft((d) => ({ ...d, [row.step]: next }))}
                          onCommit={(next) => void commit(row, next)}
                        />
                      )}
                    </td>

                    <td className="p-2 tabular-nums">
                      {row.loaded === 0
                        ? <span className="text-muted-foreground"><Bidi>{t.glNothingYet}</Bidi></span>
                        : formatCount(row.loaded, locale)}
                    </td>

                    <td className="p-2 text-muted-foreground">
                      {/* **THE ONE THING ON THIS SCREEN A PERSON CANNOT WORK OUT FOR
                          THEMSELVES**, which is why the pack gave it a column. */}
                      <Bidi>
                        {row.needs.length === 0
                          ? '—'
                          : row.needs.map((n) => t.glStep[n]).join(' · ')}
                      </Bidi>
                    </td>

                    <td className="p-2">
                      <span
                        className="inline-block rounded border px-1.5 py-0.5 text-[11px] font-semibold"
                        style={{ color: tone.fg, background: tone.bg, borderColor: tone.border }}
                      >
                        <Bidi>{t.glState[row.state]}</Bidi>
                      </span>
                      {/* **HOW MANY, NOT JUST THAT IT IS SHORT.** "1 missing" is a job
                          somebody does before lunch; "short" is a job somebody schedules. */}
                      {missing > 0 && row.state === 'SHORT' ? (
                        <span className="ms-2 text-[11.5px] text-muted-foreground">
                          <Bidi>
                            {fill(
                              pluralOf(t.glMissing, missing, locale),
                              { count: formatCount(missing, locale) },
                            )}
                          </Bidi>
                        </span>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* **THE TWO NOTES ARE DRAWN ONLY WHEN THEY ARE TRUE.** A sentence explaining
          "Blocked" printed under a table with no blocked row teaches the reader to skip
          the notes, and then the one that matters goes unread. */}
      {(hasBlocked || hasUntargeted) && (
        <Card>
          <div className="flex flex-col gap-2 p-3.5 text-[12.5px] leading-relaxed text-muted-foreground">
            {hasBlocked ? <p className="max-w-[80ch]">{t.glSignInNote}</p> : null}
            {hasUntargeted ? <p className="max-w-[80ch]">{t.glNoTargetNote}</p> : null}
          </div>
        </Card>
      )}
    </div>
  )
}

/** The plural form for a count, in the reader's own language. */
function pluralOf(
  forms: Record<string, string>, count: number, locale: 'en' | 'ar',
): string {
  const rules = new Intl.PluralRules(locale === 'ar' ? 'ar' : 'en')
  return forms[rules.select(count)] ?? forms.other ?? ''
}
